import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';

function ElitePractice() {
  const [selectedSubject, setSelectedSubject] = useState('Reading');
  const [userPremiumStatus, setUserPremiumStatus] = useState(false);
  const [userExamType, setUserExamType] = useState('selective');
  const [loading, setLoading] = useState(true);
  const [allExams, setAllExams] = useState([]);
  const [groupedTests, setGroupedTests] = useState({});

  useEffect(() => {
    checkPremiumStatus();
    fetchExams();
  }, []);

  const fetchExams = async () => {
    try {
      const response = await apiClient.get('/exams');
      const paidBundleExams = response.data.filter(exam => exam.category === 'PaidBundle');
      setAllExams(paidBundleExams);
      groupTestsByTopic(paidBundleExams);
    } catch (err) {
      console.error('Error fetching exams:', err);
    }
  };

  const groupTestsByTopic = (exams) => {
    const grouped = {};
    
    exams.forEach(exam => {
      // Normalize topic name: remove OC_ prefix for grouping
      let subject = exam.topic;
      if (subject.startsWith('OC_')) {
        subject = subject.replace('OC_', '');
        // Convert MathematicalReasoning to Mathematical Reasoning
        if (subject === 'MathematicalReasoning') {
          subject = 'Mathematical Reasoning';
        } else if (subject === 'ThinkingSkills') {
          subject = 'Thinking Skills';
        }
      } else {
        // For Selective exams, also normalize
        if (subject === 'MathematicalReasoning') {
          subject = 'Mathematical Reasoning';
        } else if (subject === 'ThinkingSkills') {
          subject = 'Thinking Skills';
        }
      }
      
      if (!grouped[subject]) {
        grouped[subject] = [];
      }
      
      grouped[subject].push(exam);
    });
    
    // Sort tests within each subject by examId
    Object.keys(grouped).forEach(subject => {
      grouped[subject].sort((a, b) => {
        const getTestNumber = (examId) => {
          const match = examId.match(/-(\d+)$/);
          return match ? parseInt(match[1]) : 0;
        };
        return getTestNumber(a.examId) - getTestNumber(b.examId);
      });
    });
    
    setGroupedTests(grouped);
  };

  const checkPremiumStatus = async () => {
    try {
      const { fetchUserAttributes } = await import('aws-amplify/auth');
      const attributes = await fetchUserAttributes();
      
      const isPremium = attributes['custom:premium_status'] === 'true';
      setUserPremiumStatus(isPremium);
      setUserExamType(attributes['custom:exam_type'] || 'selective');
    } catch (err) {
      console.error('Error checking premium status:', err);
      setUserPremiumStatus(false);
    } finally {
      setLoading(false);
    }
  };

  // Subject configuration with colors matching ExamList
  const subjectConfig = {
    'Reading': { color: '#10b981', icon: '📖' },
    'Mathematical Reasoning': { color: '#4f46e5', icon: '🔢' },
    'Thinking Skills': { color: '#f59e0b', icon: '🧠' },
    'Writing': { color: '#ef4444', icon: '✍️' }
  };

  const subjects = userExamType === 'oc' 
    ? ['Reading', 'Mathematical Reasoning', 'Thinking Skills']
    : ['Reading', 'Mathematical Reasoning', 'Thinking Skills', 'Writing'];

  // Get tests for selected subject
  const getTestsForSubject = () => {
    return groupedTests[selectedSubject] || [];
  };

  // Get current subject config
  const getCurrentSubjectConfig = () => {
    return subjectConfig[selectedSubject] || { color: '#4f46e5', icon: '💎' };
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
        <p style={{ fontSize: '18px', color: '#666' }}>Loading elite tests...</p>
      </div>
    );
  }

  if (!userPremiumStatus) {
    return (
      <div style={{ maxWidth: '800px', margin: '60px auto', padding: '20px' }}>
        <div className="card" style={{ 
          textAlign: 'center', 
          padding: '60px 40px',
          background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
          color: 'white'
        }}>
          <div style={{ fontSize: '80px', marginBottom: '20px' }}>💎</div>
          <h1 style={{ fontSize: '36px', marginBottom: '20px' }}>Elite Access Required</h1>
          <p style={{ fontSize: '18px', marginBottom: '30px', opacity: '0.95', lineHeight: '1.7' }}>
            Elite Practice tests are available exclusively to premium members. 
            Upgrade your account to access 7 additional full-length mock tests for each subject!
          </p>
          <div style={{ 
            background: 'rgba(255,255,255,0.2)', 
            padding: '25px', 
            borderRadius: '12px',
            marginBottom: '30px'
          }}>
            <h3 style={{ fontSize: '20px', marginBottom: '15px' }}>What You Get:</h3>
            <ul style={{ 
              textAlign: 'left', 
              fontSize: '16px', 
              lineHeight: '2',
              listStyle: 'none',
              padding: 0
            }}>
              <li>✅ 7 elite tests per subject (28 total)</li>
              <li>✅ Full-length mock exams matching NSW format</li>
              <li>✅ More challenging questions</li>
              <li>✅ Detailed performance analytics</li>
              <li>✅ All topic-based tests included</li>
            </ul>
          </div>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/pricing" style={{ textDecoration: 'none' }}>
              <button 
                className="btn-primary"
                style={{ 
                  background: 'white',
                  color: '#ef4444',
                  padding: '15px 40px',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
              >
                Upgrade to Premium
              </button>
            </Link>
            <Link to="/exams" style={{ textDecoration: 'none' }}>
              <button 
                className="btn-secondary"
                style={{ 
                  background: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: '2px solid white',
                  padding: '15px 40px',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
              >
                Try Free Tests
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1400px', margin: '40px auto', padding: '20px' }}>
      {/* Header */}
      <div className="card" style={{ 
        textAlign: 'center', 
        padding: '50px 24px', 
        marginBottom: '30px',
        background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
        color: 'white'
      }}>
        <div style={{ fontSize: '56px', marginBottom: '15px' }}>💎</div>
        <h1 style={{ fontSize: '42px', marginBottom: '15px' }}>Elite Practice</h1>
        <p style={{ fontSize: '20px', opacity: '0.95', maxWidth: '700px', margin: '0 auto' }}>
          Exclusive full-length tests for premium members. Challenge yourself with more practice tests!
        </p>
      </div>

      {/* Subject Selector */}
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '24px', marginBottom: '15px', color: '#ef4444', textAlign: 'center' }}>
          Choose a Subject
        </h2>
        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {subjects.map(subject => {
            const config = subjectConfig[subject];
            return (
              <button
                key={subject}
                onClick={() => setSelectedSubject(subject)}
                style={{
                  padding: '15px 30px',
                  borderRadius: '12px',
                  border: selectedSubject === subject ? `3px solid ${config.color}` : '3px solid #e5e7eb',
                  background: selectedSubject === subject ? config.color : 'white',
                  color: selectedSubject === subject ? 'white' : config.color,
                  fontSize: '18px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  boxShadow: selectedSubject === subject ? `0 6px 12px ${config.color}40` : '0 2px 4px rgba(0,0,0,0.1)'
                }}
                onMouseEnter={(e) => {
                  if (selectedSubject !== subject) {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedSubject !== subject) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                  }
                }}
              >
                {config.icon} {subject}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tests Grid */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: '25px',
        marginBottom: '40px'
      }}>
        {getTestsForSubject().map((test, index) => {
          const currentConfig = getCurrentSubjectConfig();
          return (
            <div 
              key={test.examId}
              className="card" 
              style={{ 
                padding: '30px',
                background: 'white',
                border: `3px solid ${currentConfig.color}`,
                transition: 'all 0.3s',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = `0 12px 24px ${currentConfig.color}40`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Premium Badge */}
              <div style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: currentConfig.color,
                color: 'white',
                padding: '6px 14px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}>
                <span>💎</span>
                <span>ELITE</span>
              </div>
              
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>
                {currentConfig.icon}
              </div>
              
              <h4 style={{ 
                fontSize: '20px', 
                marginBottom: '12px', 
                color: currentConfig.color,
                fontWeight: 'bold',
                paddingRight: '100px'
              }}>
                {test.title}
              </h4>
              
              <p style={{ 
                fontSize: '14px', 
                lineHeight: '1.6', 
                color: '#666',
                marginBottom: '20px',
                minHeight: '44px'
              }}>
                {test.description}
              </p>
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '15px',
                background: `${currentConfig.color}15`,
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px', fontWeight: '600' }}>Questions</div>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: currentConfig.color }}>
                    {test.questionCount}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px', fontWeight: '600' }}>Duration</div>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: currentConfig.color }}>
                    {test.duration}m
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px', fontWeight: '600' }}>Test</div>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: currentConfig.color }}>
                    #{index + 4}
                  </div>
                </div>
              </div>
              
              <Link to={`/exam/${test.examId}`} style={{ textDecoration: 'none' }}>
                <button 
                  className="btn-primary"
                  style={{ 
                    width: '100%', 
                    padding: '14px', 
                    fontSize: '16px',
                    fontWeight: 'bold',
                    background: currentConfig.color,
                    border: 'none'
                  }}
                >
                  Start Elite Test
                </button>
              </Link>
            </div>
          );
        })}
      </div>

      {getTestsForSubject().length === 0 && (
        <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>💎</div>
          <h3 style={{ fontSize: '24px', color: '#666', marginBottom: '10px' }}>No elite tests available yet</h3>
          <p style={{ fontSize: '16px', color: '#999' }}>
            Elite tests for {selectedSubject} are coming soon!
          </p>
        </div>
      )}

      {/* Info Banner */}
      <div className="card" style={{ 
        marginTop: '40px',
        padding: '30px',
        background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '36px', marginBottom: '15px' }}>🎯</div>
        <h3 style={{ fontSize: '24px', marginBottom: '12px' }}>Elite Test Features</h3>
        <p style={{ fontSize: '16px', opacity: '0.95', maxWidth: '800px', margin: '0 auto', lineHeight: '1.7' }}>
          Each elite test is a full-length mock exam designed to match the NSW Selective High School Test format. 
          Questions are carefully crafted at Year 7-8 difficulty level to challenge and prepare you for the actual exam. 
          Track your progress across all tests and identify areas for improvement!
        </p>
      </div>
    </div>
  );
}

export default ElitePractice;
